import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './GoalsDisplay.css';

/**
 * GoalsDisplay Widget Component
 * 
 * Modular, reusable widget for displaying P&L goals and progress.
 * Can be easily moved to other pages or repositioned.
 * 
 * Props:
 * - equityData: Array of equity records
 * - userSettings: User settings object with goals
 * - mode: 'equity' or 'realized' (default: 'equity')
 * - onModeChange: Callback when mode changes (optional)
 * - showYearChart: Boolean to show/hide year progress chart (default: true)
 * - onRefresh: Callback to refresh data (optional)
 */
function GoalsDisplay({ equityData = [], userSettings = null, mode: propMode = null, onModeChange = null, showYearChart = true, onRefresh = null, selectedAccount = 'all', accounts = [] }) {
  // Load preferences from localStorage
  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem('goalsDisplay_preferences');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading goals preferences:', e);
    }
    return null;
  };

  // Save preferences to localStorage
  const savePreferences = (prefs) => {
    try {
      localStorage.setItem('goalsDisplay_preferences', JSON.stringify(prefs));
    } catch (e) {
      console.error('Error saving goals preferences:', e);
    }
  };

  // Load saved preferences
  const savedPrefs = loadPreferences();
  
  // Use propMode if provided (from parent), otherwise use saved preference, otherwise default
  const [mode, setMode] = useState(() => {
    // If propMode is explicitly provided, use it (parent controls it)
    if (propMode !== null && propMode !== undefined) {
      return propMode;
    }
    // Otherwise use saved preference or default
    return savedPrefs?.pnlMode || 'equity';
  });

  // Sync mode with propMode if it changes from parent (when parent manages state)
  useEffect(() => {
    if (propMode !== null && propMode !== undefined && propMode !== mode) {
      setMode(propMode);
    }
  }, [propMode, mode]);
  const [realizedPnL, setRealizedPnL] = useState({
    weekly: null,
    monthly: null,
    yearly: null
  });
  const [loadingRealized, setLoadingRealized] = useState(false);
  const [historyView, setHistoryView] = useState(savedPrefs?.historyView || 'months'); // 'none', 'days', 'weeks', 'months'
  const [historyPeriods, setHistoryPeriods] = useState(savedPrefs?.historyPeriods || 12); // Number of periods to show or 'all'
  const [historicalRealizedPnL, setHistoricalRealizedPnL] = useState([]); // Historical realized P&L data

  // Handle mode change and save preference
  const handleModeChange = (newMode) => {
    setMode(newMode);
    const prefs = { ...savedPrefs, pnlMode: newMode };
    savePreferences(prefs);
    if (onModeChange) {
      onModeChange(newMode);
    }
  };

  // Handle history view change and save preference
  const handleHistoryViewChange = (newView) => {
    setHistoryView(newView);
    const prefs = { ...(loadPreferences() || {}), historyView: newView };
    savePreferences(prefs);
  };

  // Handle history periods change and save preference
  const handleHistoryPeriodsChange = (newPeriods) => {
    const periodsValue = newPeriods === 'all' ? 'all' : parseInt(newPeriods);
    setHistoryPeriods(periodsValue);
    const prefs = { ...(loadPreferences() || {}), historyPeriods: periodsValue };
    savePreferences(prefs);
  };

  // Fetch realized P&L when mode is 'realized'
  useEffect(() => {
    if (mode === 'realized' && userSettings) {
      fetchGoalsData();
    } else {
      setRealizedPnL({ weekly: null, monthly: null, yearly: null });
      setHistoricalRealizedPnL([]);
    }
  }, [mode, equityData, userSettings, historyView, historyPeriods, selectedAccount, accounts]);

  // Consolidated function to fetch all goals data in one API call
  const fetchGoalsData = async () => {
    try {
      setLoadingRealized(true);
      
      // Performance measurement
      const performanceStart = performance.now();
      const requestStart = Date.now();
      
      // Get account_id if a specific account is selected
      let accountIdParam = null;
      if (selectedAccount && selectedAccount !== 'all' && accounts.length > 0) {
        const account = accounts.find(acc => 
          acc.id === parseInt(selectedAccount) || acc.broker_account_id === selectedAccount
        );
        if (account) {
          accountIdParam = account.broker_account_id;
        }
      }
      
      // Build params for consolidated endpoint
      const params = {
        history_view: historyView !== 'none' ? historyView : 'months',
        history_periods: historyPeriods
      };
      if (accountIdParam) {
        params.account_id = accountIdParam;
      }
      
      // Single API call to get all data
      const response = await axios.get(`${API_URL}/portfolio/trades/goals_data/`, { params });
      
      // Performance measurement
      const performanceEnd = performance.now();
      const requestEnd = Date.now();
      const totalTime = performanceEnd - performanceStart;
      const networkTime = requestEnd - requestStart;
      
      console.log(`[Goals Data] New endpoint performance:`, {
        totalTime: `${totalTime.toFixed(2)}ms`,
        networkTime: `${networkTime}ms`,
        historyView,
        historyPeriods,
        historicalCount: response.data.historical?.length || 0
      });
      
      // Set current periods
      setRealizedPnL({
        weekly: parseFloat(response.data.current_periods.weekly) || 0,
        monthly: parseFloat(response.data.current_periods.monthly) || 0,
        yearly: parseFloat(response.data.current_periods.yearly) || 0
      });
      
      // Set historical data if history view is enabled
      if (historyView !== 'none') {
        // Transform backend data to match frontend format
        // Filter out months/periods where no trades were closed (realized_pnl is 0 or null)
        const transformedHistorical = response.data.historical
          .filter(item => {
            const pnl = parseFloat(item.realized_pnl) || 0;
            return pnl !== 0; // Only include periods with trades
          })
          .map(item => {
            const result = {
              period: item.period,
              date: new Date(item.date),
              [item.period_type]: parseFloat(item.realized_pnl) || 0
            };
            return result;
          });
        setHistoricalRealizedPnL(transformedHistorical);
      } else {
        setHistoricalRealizedPnL([]);
      }
    } catch (err) {
      console.error('Error fetching goals data:', err);
      setRealizedPnL({ weekly: 0, monthly: 0, yearly: 0 });
      setHistoricalRealizedPnL([]);
    } finally {
      setLoadingRealized(false);
    }
  };

  // Parse IBKR date format (DD/MM/YY)
  const parseIBKRDate = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const fullYear = year < 50 ? 2000 + year : 1900 + year;
      return new Date(fullYear, month, day);
    }
    return null;
  };

  // Helper: Check if date is a trading day (Monday-Friday)
  const isTradingDay = (date) => {
    const day = date.getDay();
    return day >= 1 && day <= 5; // Monday = 1, Friday = 5
  };

  // Helper: Get start of current week (Monday)
  const getWeekStart = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
    return new Date(d.setDate(diff));
  };

  // Calculate P&L from equity data for a specific period
  const calculateEquityPnL = (period) => {
    if (!equityData || equityData.length === 0) return 0;
    
    const now = new Date();
    let startDate = null;
    
    if (period === 'weekly') {
      // Current week: Monday to today
      // Calculate difference between latest equity and equity at start of week
      const weekStart = getWeekStart(now);
      // Normalize dates to midnight for comparison (remove time component)
      const weekStartDate = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate());
      const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      // Find the latest equity (first in array, sorted newest first)
      // Apply equity adjustment if available
      const equityAdjustment = userSettings?.equity_adjustment ? parseFloat(userSettings.equity_adjustment) : 0;
      const rawLatestEquity = equityData.length > 0 ? parseFloat(equityData[0].total) : 0;
      const latestEquity = rawLatestEquity - equityAdjustment;
      
      // Find equity at start of week (or closest date before/on week start)
      // Since equityData is sorted newest first, we want the most recent date <= weekStart
      let weekStartEquity = null;
      for (const day of equityData) {
        const dayDate = parseIBKRDate(day.report_date);
        if (!dayDate) continue;
        
        // Normalize to date only (remove time)
        const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        
        // Find the most recent equity record on or before week start
        if (dayDateOnly <= weekStartDate) {
          weekStartEquity = parseFloat(day.total);
          break;
        }
      }
      
      // If we found a week start equity, calculate difference
      if (weekStartEquity !== null) {
        return latestEquity - weekStartEquity;
      }
      
      // Fallback: sum total_diff for days in the week
      const relevantDays = equityData.filter(day => {
        const dayDate = parseIBKRDate(day.report_date);
        if (!dayDate) return false;
        const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        return dayDateOnly >= weekStartDate && dayDateOnly <= todayDate;
      });
      
      return relevantDays.reduce((sum, day) => {
        return sum + (parseFloat(day.total_diff) || 0);
      }, 0);
    } else if (period === 'monthly') {
      // Current calendar month: 1st of current month to today
      // Example: Jan 1-31 for January, Feb 1-28/29 for February (not "last 30 days")
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const monthStartDate = new Date(currentYear, currentMonth, 1);
      const todayDate = new Date(currentYear, currentMonth, now.getDate());
      
      // Calculate difference between latest equity and equity at start of current month
      // Apply equity adjustment if available
      const equityAdjustment = userSettings?.equity_adjustment ? parseFloat(userSettings.equity_adjustment) : 0;
      const rawLatestEquity = equityData.length > 0 ? parseFloat(equityData[0].total) : 0;
      const latestEquity = rawLatestEquity - equityAdjustment;
      
      // Find equity at start of current month (or closest date before/on month start)
      let monthStartEquity = null;
      for (const day of equityData) {
        const dayDate = parseIBKRDate(day.report_date);
        if (!dayDate) continue;
        
        const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        // Only consider dates from the current month or before
        if (dayDate.getFullYear() === currentYear && dayDate.getMonth() === currentMonth) {
          // This is in the current month, check if it's the first day or before
          if (dayDateOnly <= monthStartDate) {
            monthStartEquity = parseFloat(day.total);
            break;
          }
        } else if (dayDate.getFullYear() < currentYear || 
                   (dayDate.getFullYear() === currentYear && dayDate.getMonth() < currentMonth)) {
          // This is from a previous month/year, use it as month start equity
          monthStartEquity = parseFloat(day.total);
          break;
        }
      }
      
      if (monthStartEquity !== null) {
        return latestEquity - monthStartEquity;
      }
      
      // Fallback: sum total_diff only for days in the current calendar month
      const relevantDays = equityData.filter(day => {
        const dayDate = parseIBKRDate(day.report_date);
        if (!dayDate) return false;
        // Explicitly check that the date is in the current month and year
        if (dayDate.getFullYear() !== currentYear || dayDate.getMonth() !== currentMonth) {
          return false;
        }
        const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        return dayDateOnly >= monthStartDate && dayDateOnly <= todayDate;
      });
      
      return relevantDays.reduce((sum, day) => {
        return sum + (parseFloat(day.total_diff) || 0);
      }, 0);
    } else if (period === 'yearly') {
      // Current year: January 1st to today
      // Define date range for the current year
      const currentYear = now.getFullYear();
      const yearStartDate = new Date(currentYear, 0, 1);
      const todayDate = new Date(currentYear, now.getMonth(), now.getDate());
      
      // Apply equity adjustment if available
      const equityAdjustment = userSettings?.equity_adjustment ? parseFloat(userSettings.equity_adjustment) : 0;
      const rawLatestEquity = equityData.length > 0 ? parseFloat(equityData[0].total) : 0;
      const latestEquity = rawLatestEquity - equityAdjustment;
      
      // Use yearly_start_equity from userSettings if available (more accurate)
      let yearStartEquity = null;
      if (userSettings && userSettings.yearly_start_equity) {
        yearStartEquity = parseFloat(userSettings.yearly_start_equity);
      } else {
        // Fallback: find from equityData
        startDate = new Date(now.getFullYear(), 0, 1);
        const yearStartDateForSearch = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        for (const day of equityData) {
          const dayDate = parseIBKRDate(day.report_date);
          if (!dayDate) continue;
          
          const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
          if (dayDateOnly <= yearStartDateForSearch) {
            yearStartEquity = parseFloat(day.total);
            break;
          }
        }
      }
      
      if (yearStartEquity !== null) {
        return latestEquity - yearStartEquity;
      }
      
      // Fallback: sum total_diff
      const relevantDays = equityData.filter(day => {
        const dayDate = parseIBKRDate(day.report_date);
        if (!dayDate) return false;
        const dayDateOnly = new Date(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
        return dayDateOnly >= yearStartDate && dayDateOnly <= todayDate;
      });
      
      return relevantDays.reduce((sum, day) => {
        return sum + (parseFloat(day.total_diff) || 0);
      }, 0);
    } else {
      // Fallback: last N days
      const days = period === 'last7' ? 7 : period === 'last30' ? 30 : 365;
      const relevantDays = equityData.slice(0, days);
      return relevantDays.reduce((sum, day) => {
        return sum + (parseFloat(day.total_diff) || 0);
      }, 0);
    }
  };

  // Get current P&L based on mode
  const getCurrentPnL = (period) => {
    if (mode === 'realized') {
      return realizedPnL[period] || 0;
    } else {
      // Equity-based: use actual period (weekly/monthly/yearly)
      return calculateEquityPnL(period);
    }
  };

  // Get yearly goal (amount or percentage-based)
  const getYearlyGoal = () => {
    if (!userSettings || userSettings === false) return null;
    
    if (userSettings.yearly_goal_type === 'percentage' && userSettings.yearly_pnl_goal_percentage && userSettings.yearly_start_equity) {
      // Calculate goal as percentage of start equity
      const percentage = parseFloat(userSettings.yearly_pnl_goal_percentage);
      const startEquity = parseFloat(userSettings.yearly_start_equity);
      return (startEquity * percentage) / 100;
    } else if (userSettings.yearly_pnl_goal) {
      // Use absolute amount
      return parseFloat(userSettings.yearly_pnl_goal);
    }
    return null;
  };

  // Get minimum yearly goal (amount or percentage-based)
  const getMinimumYearlyGoal = () => {
    if (!userSettings || userSettings === false) return null;
    
    if (userSettings.yearly_minimum_goal_type === 'percentage' && userSettings.yearly_minimum_goal_percentage && userSettings.yearly_start_equity) {
      const percentage = parseFloat(userSettings.yearly_minimum_goal_percentage);
      const startEquity = parseFloat(userSettings.yearly_start_equity);
      return (startEquity * percentage) / 100;
    } else if (userSettings.yearly_minimum_goal) {
      return parseFloat(userSettings.yearly_minimum_goal);
    }
    return null;
  };

  // Get stretch yearly goal (amount or percentage-based)
  const getStretchYearlyGoal = () => {
    if (!userSettings || userSettings === false) return null;
    
    if (userSettings.yearly_stretch_goal_type === 'percentage' && userSettings.yearly_stretch_goal_percentage && userSettings.yearly_start_equity) {
      const percentage = parseFloat(userSettings.yearly_stretch_goal_percentage);
      const startEquity = parseFloat(userSettings.yearly_start_equity);
      return (startEquity * percentage) / 100;
    } else if (userSettings.yearly_stretch_goal) {
      return parseFloat(userSettings.yearly_stretch_goal);
    }
    return null;
  };

  // Calculate progress percentage
  const getProgress = (current, goal) => {
    if (!goal || goal === 0) return null;
    return (current / goal) * 100;
  };

  // Get color based on progress
  const getProgressColor = (progress) => {
    if (progress === null) return 'gray';
    if (progress >= 100) return 'green';
    if (progress >= 50) return 'orange';
    return 'red';
  };

  // Format currency
  const selectedAccountData = accounts.find(a =>
    a.id === parseInt(selectedAccount) || a.broker_account_id === selectedAccount
  );
  const currencySymbol = selectedAccountData?.broker === 'tastytrade' ? '$' : '€';

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol}0.00`;
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencySymbol}${formattedNumber}`;
  };

  // Calculate year progress data for chart
  const getYearProgressData = () => {
    // Check for both amount and percentage-based goals
    const hasYearlyGoal = userSettings && (
      (userSettings.yearly_goal_type === 'amount' && userSettings.yearly_pnl_goal) ||
      (userSettings.yearly_goal_type === 'percentage' && userSettings.yearly_pnl_goal_percentage && userSettings.yearly_start_equity)
    );
    
    if (!hasYearlyGoal || !userSettings.yearly_start_equity) {
      return null;
    }

    const now = new Date();
    const yearStart = new Date(now.getFullYear(), 0, 1);
    const yearEnd = new Date(now.getFullYear(), 11, 31);
    const daysInYear = Math.ceil((yearEnd - yearStart) / (1000 * 60 * 60 * 24));
    const daysElapsed = Math.ceil((now - yearStart) / (1000 * 60 * 60 * 24));
    const yearProgress = (daysElapsed / daysInYear) * 100;

    // Calculate target equity at current point (linear progression)
    const startEquity = parseFloat(userSettings.yearly_start_equity);
    const goalPnL = getYearlyGoal();
    if (!goalPnL) return null;
    const targetEquity = startEquity + (goalPnL * (yearProgress / 100));

    // Get minimum and stretch goals
    const minimumGoalPnL = getMinimumYearlyGoal();
    const stretchGoalPnL = getStretchYearlyGoal();

    // Get actual equity (latest from equityData) and apply adjustment
    const equityAdjustment = userSettings.equity_adjustment ? parseFloat(userSettings.equity_adjustment) : 0;
    const rawLatestEquity = equityData.length > 0 ? parseFloat(equityData[0].total) : startEquity;
    const latestEquity = rawLatestEquity - equityAdjustment;
    const actualPnL = latestEquity - startEquity;

    // Calculate end-of-year target equity for all goals
    const endTargetEquity = startEquity + goalPnL;
    const endMinimumEquity = minimumGoalPnL ? startEquity + minimumGoalPnL : null;
    const endStretchEquity = stretchGoalPnL ? startEquity + stretchGoalPnL : null;
    
    // Calculate current target equity for all goals
    const currentMinimumEquity = minimumGoalPnL ? startEquity + (minimumGoalPnL * (yearProgress / 100)) : null;
    const currentStretchEquity = stretchGoalPnL ? startEquity + (stretchGoalPnL * (yearProgress / 100)) : null;
    
    // Generate chart data points (monthly)
    // Target line should start at startEquity (Jan 1) and end at endTargetEquity (Dec 31)
    const chartData = [];
    for (let month = 0; month <= 11; month++) {
      const monthDate = new Date(now.getFullYear(), month, 15); // Middle of month
      // Linear progression: start at startEquity (month 0 = Jan), end at endTargetEquity (month 11 = Dec)
      const monthProgress = month / 11; // 0 for Jan, 1 for Dec
      const monthTargetEquity = startEquity + (goalPnL * monthProgress);
      const monthMinimumEquity = minimumGoalPnL ? startEquity + (minimumGoalPnL * monthProgress) : null;
      const monthStretchEquity = stretchGoalPnL ? startEquity + (stretchGoalPnL * monthProgress) : null;
      
      // Find actual equity for this month (or closest)
      let monthActualEquity = startEquity;
      if (equityData.length > 0) {
        const monthStr = `${String(month + 1).padStart(2, '0')}/01/${String(now.getFullYear()).slice(-2)}`;
        const monthEquity = equityData.find(e => {
          const equityDate = parseIBKRDate(e.report_date);
          return equityDate && equityDate.getMonth() === month && equityDate.getFullYear() === now.getFullYear();
        });
        if (monthEquity) {
          // Apply equity adjustment to historical data too
          monthActualEquity = parseFloat(monthEquity.total) - equityAdjustment;
        } else {
          // Use latest if not found (already has adjustment applied)
          monthActualEquity = latestEquity;
        }
      }
      
      const dataPoint = {
        month: monthDate.toLocaleDateString('nl-NL', { month: 'short' }),
        target: monthTargetEquity,
        actual: monthActualEquity
      };
      
      if (monthMinimumEquity !== null) {
        dataPoint.minimum = monthMinimumEquity;
      }
      if (monthStretchEquity !== null) {
        dataPoint.stretch = monthStretchEquity;
      }
      
      chartData.push(dataPoint);
    }

    return {
      chartData,
      yearProgress,
      targetEquity, // Current target based on year progress
      endTargetEquity, // End-of-year target (startEquity + goalPnL)
      actualEquity: latestEquity,
      actualPnL,
      startEquity,
      goalPnL,
      minimumGoalPnL,
      stretchGoalPnL,
      currentMinimumEquity,
      currentStretchEquity,
      endMinimumEquity,
      endStretchEquity
    };
  };

  const yearData = getYearProgressData();

  // Calculate historical data
  const getHistoricalData = () => {
    // If mode is 'realized' and we have historical realized P&L data, use it
    if (mode === 'realized' && historicalRealizedPnL.length > 0) {
      // Sort by date descending (latest first)
      return [...historicalRealizedPnL].sort((a, b) => b.date - a.date);
    }
    
    if (!equityData || equityData.length === 0) return [];
    
    const now = new Date();
    const data = [];
    
    if (historyView === 'days') {
      // Last N days - show daily P&L
      const daysToShow = historyPeriods === 'all' ? equityData.length : historyPeriods;
      const days = equityData.slice(0, daysToShow);
      days.forEach((day, index) => {
        const dayDate = parseIBKRDate(day.report_date);
        if (!dayDate) return;
        
        // For daily view, we'll calculate daily P&L (difference from previous day)
        // This will be calculated in the render function
        data.push({
          period: dayDate.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' }),
          date: dayDate,
          daily: parseFloat(day.total_diff) || 0
        });
      });
    } else if (historyView === 'weeks') {
      // Last N weeks
      const weeksToFetch = historyPeriods === 'all' ? 52 : historyPeriods; // Max 52 weeks (1 year) for 'all'
      for (let i = 0; i < weeksToFetch; i++) {
        const weekEnd = new Date(now);
        weekEnd.setDate(weekEnd.getDate() - (i * 7));
        const weekStart = getWeekStart(weekEnd);
        
        const weekData = equityData.filter(e => {
          const eDate = parseIBKRDate(e.report_date);
          return eDate && eDate >= weekStart && eDate <= weekEnd;
        });
        
        if (weekData.length > 0) {
          // Calculate weekly P&L as difference between first and last day of week
          const firstDay = weekData[weekData.length - 1]; // Oldest
          const lastDay = weekData[0]; // Newest
          const weekPnL = (parseFloat(lastDay.total) || 0) - (parseFloat(firstDay.total) || 0);
          data.push({
            period: `${weekStart.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })} - ${weekEnd.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' })}`,
            date: weekEnd,
            weekly: weekPnL
          });
        }
      }
      } else if (historyView === 'months') {
        // Last N months
        let monthsToFetch = historyPeriods;
        if (historyPeriods === 'all') {
          // Calculate months from oldest equity data to now
          if (equityData && equityData.length > 0) {
            const oldestDate = parseIBKRDate(equityData[equityData.length - 1].report_date);
            if (oldestDate) {
              const monthsDiff = (now.getFullYear() - oldestDate.getFullYear()) * 12 + 
                                (now.getMonth() - oldestDate.getMonth()) + 1;
              monthsToFetch = Math.max(monthsDiff, 12); // At least 12 months
            } else {
              monthsToFetch = 24; // Fallback to 24 months if date parsing fails
            }
          } else {
            monthsToFetch = 24; // Fallback to 24 months if no equity data
          }
        }
        
        for (let i = 0; i < monthsToFetch; i++) {
          const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0); // Last day of month
          const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1); // First day of month
          
          const monthData = equityData.filter(e => {
            const eDate = parseIBKRDate(e.report_date);
            return eDate && eDate >= monthStart && eDate <= monthEnd;
          });
          
          if (monthData.length > 0) {
            // Calculate monthly P&L as difference between first and last day of month
            const firstDay = monthData[monthData.length - 1]; // Oldest
            const lastDay = monthData[0]; // Newest
            const monthPnL = (parseFloat(lastDay.total) || 0) - (parseFloat(firstDay.total) || 0);
            data.push({
              period: monthEnd.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }),
              date: monthEnd,
              monthly: monthPnL
            });
          }
        }
      }
    
    // Sort by date descending (latest first) instead of reversing
    return data.sort((a, b) => b.date - a.date);
  };

  // Calculate P&L for a specific date
  const calculateEquityPnLForDate = (endDate, period) => {
    if (!equityData || equityData.length === 0) return 0;
    
    let startDate = null;
    const now = new Date();
    
    if (period === 'weekly') {
      startDate = getWeekStart(endDate);
    } else if (period === 'monthly') {
      startDate = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
    } else if (period === 'yearly') {
      startDate = new Date(endDate.getFullYear(), 0, 1);
    }
    
    if (!startDate) return 0;
    
    const relevantDays = equityData.filter(day => {
      const dayDate = parseIBKRDate(day.report_date);
      if (!dayDate) return false;
      return dayDate >= startDate && dayDate <= endDate;
    });
    
    return relevantDays.reduce((sum, day) => {
      return sum + (parseFloat(day.total_diff) || 0);
    }, 0);
  };

  const historicalData = getHistoricalData();
  const yearlyGoal = getYearlyGoal();

  // Show loading state if userSettings is still loading
  if (userSettings === null) {
    return (
      <div className="goals-display-widget">
        <div className="goals-loading">Loading goals settings...</div>
      </div>
    );
  }

  // Show message if no goals are set
  // Check if userSettings exists and has any goals
  const hasGoals = userSettings && (
    userSettings.weekly_pnl_goal || 
    userSettings.monthly_pnl_goal || 
    yearlyGoal
  );
  
  if (userSettings === false || !hasGoals) {
    return (
      <div className="goals-display-widget">
        <div className="goals-header">
          <h3>📊 Portfolio Goals</h3>
        </div>
        <div className="goals-empty-state">
          <p>No goals have been set yet.</p>
          <p>Go to <a href="/settings">Settings</a> to configure your weekly, monthly, and yearly P&L goals.</p>
          {userSettings && (
            <div style={{ marginTop: '1rem', padding: '1rem', background: '#f3f4f6', borderRadius: '6px', fontSize: '0.875rem' }}>
              <strong>Debug info:</strong>
              <pre style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                {JSON.stringify({
                  weekly: userSettings.weekly_pnl_goal,
                  monthly: userSettings.monthly_pnl_goal,
                  yearlyGoal: yearlyGoal,
                  yearly_pnl_goal: userSettings.yearly_pnl_goal,
                  yearly_pnl_goal_percentage: userSettings.yearly_pnl_goal_percentage,
                  yearly_goal_type: userSettings.yearly_goal_type
                }, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="goals-display-widget">
      <div className="goals-header">
        <h3>📊 Portfolio Goals</h3>
        <div className="goals-header-controls">
          {(onModeChange || true) && (
            <div className="goals-mode-toggle">
              <button
                className={mode === 'equity' ? 'active' : ''}
                onClick={() => handleModeChange('equity')}
                title="Shows total equity change (realized + unrealized P&L)"
              >
                Equity-based
              </button>
              <button
                className={mode === 'realized' ? 'active' : ''}
                onClick={() => handleModeChange('realized')}
                title="Shows only realized P&L from closed trades"
              >
                Realized P&L
              </button>
            </div>
          )}
          {onRefresh && (
            <button
              className="goals-refresh-button"
              onClick={onRefresh}
              title="Refresh goals and recalculate"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Year Equity Overview Block */}
      {yearlyGoal && userSettings.yearly_start_equity && (() => {
        const startEquity = parseFloat(userSettings.yearly_start_equity);
        const targetEquity = startEquity + yearlyGoal;
        // Use the actualPnL from yearData which already has equity adjustment applied
        const currentPnL = yearData ? yearData.actualPnL : getCurrentPnL('yearly');
        const currentEquity = startEquity + currentPnL;
        
        // Calculate desired yearly profit percentage
        const desiredYearlyProfitPercent = userSettings.yearly_goal_type === 'percentage' 
          ? parseFloat(userSettings.yearly_pnl_goal_percentage)
          : (yearlyGoal / startEquity) * 100;
        
        // Calculate YTD profit percentage (current P&L / start equity)
        const ytdProfitPercent = startEquity > 0 ? (currentPnL / startEquity) * 100 : 0;
        
        // Calculate percentage of yearly goal achieved
        const goalProgressPercent = yearlyGoal > 0 ? (currentPnL / yearlyGoal) * 100 : 0;
        
        return (
          <div className="year-equity-overview">
            <div className="year-equity-header">
              <h4>Yearly Equity Goals</h4>
            </div>
            <div className="year-equity-content">
              <div className="equity-info-block">
                <div className="equity-label">Start Equity (Jan 1)</div>
                <div className="equity-value">{formatCurrency(startEquity)}</div>
              </div>
              <div className="equity-arrow">→</div>
              <div className="equity-info-block">
                <div className="equity-label">Target Equity (Dec 31)</div>
                <div className="equity-value target-equity">
                  {formatCurrency(yearData ? yearData.endTargetEquity : targetEquity)}
                </div>
                <div className="equity-sub-info">
                  Target: {desiredYearlyProfitPercent.toFixed(2)}% return
                </div>
              </div>
              <div className="equity-stat-block">
                <div className="equity-stat-label">YTD Profit</div>
                <div className={`equity-stat-value ${currentPnL >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(currentPnL)}
                </div>
                <div className={`equity-stat-percent ${ytdProfitPercent >= 0 ? 'positive' : 'negative'}`}>
                  {ytdProfitPercent >= 0 ? '+' : ''}{ytdProfitPercent.toFixed(2)}%
                </div>
              </div>
              <div className="equity-stat-block">
                <div className="equity-stat-label">Goal Progress</div>
                <div className={`equity-stat-value ${goalProgressPercent >= 100 ? 'positive' : goalProgressPercent >= 50 ? 'warning' : 'negative'}`}>
                  {goalProgressPercent.toFixed(1)}%
                </div>
                <div className="equity-stat-sub">
                  of {desiredYearlyProfitPercent.toFixed(2)}% target
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <div className="goals-cards">
        {userSettings.weekly_pnl_goal && (
          <GoalCard
            title={`Weekly Goal${mode === 'equity' ? ' (Equity)' : ' (Realized)'}`}
            current={getCurrentPnL('weekly')}
            goal={parseFloat(userSettings.weekly_pnl_goal)}
            loading={mode === 'realized' && loadingRealized}
            mode={mode}
            currencySymbol={currencySymbol}
          />
        )}
        
        {userSettings.monthly_pnl_goal && (
          <GoalCard
            title={`Monthly Goal${mode === 'equity' ? ' (Equity)' : ' (Realized)'}`}
            current={getCurrentPnL('monthly')}
            goal={parseFloat(userSettings.monthly_pnl_goal)}
            loading={mode === 'realized' && loadingRealized}
            mode={mode}
            currencySymbol={currencySymbol}
          />
        )}
        
        {yearlyGoal && (
          <>
            <GoalCard
              title={`Year Goal${mode === 'equity' ? ' (Equity)' : ' (Realized)'}`}
              current={getCurrentPnL('yearly')}
              goal={yearlyGoal}
              loading={mode === 'realized' && loadingRealized}
              goalLabel={userSettings.yearly_goal_type === 'percentage'
                ? `${userSettings.yearly_pnl_goal_percentage}% of start equity`
                : null}
              mode={mode}
              currencySymbol={currencySymbol}
            />
            <YearlyGoalGauge
              current={getCurrentPnL('yearly')}
              goal={yearlyGoal}
              loading={mode === 'realized' && loadingRealized}
              goalLabel={userSettings.yearly_goal_type === 'percentage'
                ? `${userSettings.yearly_pnl_goal_percentage}% of start equity`
                : null}
              mode={mode}
              currencySymbol={currencySymbol}
            />
          </>
        )}
      </div>

      {/* Year Progress and Historical Progress Side by Side */}
      <div className="goals-progress-container">
        {showYearChart && yearData && (
          <div className="year-progress-chart">
            <h4>Year Progress</h4>
            <div className="year-progress-info">
              <div className="progress-stat">
                <span className="label">Year P&L:</span>
                <span className={`value ${yearData.actualPnL >= 0 ? 'positive' : 'negative'}`}>
                  {yearData.actualPnL >= 0 ? '+' : ''}{formatCurrency(yearData.actualPnL)}
                </span>
              </div>
              <div className="progress-stat">
                <span className="label">Actual Equity:</span>
                <span className={`value ${yearData.actualEquity >= yearData.targetEquity ? 'positive' : 'negative'}`}>
                  {formatCurrency(yearData.actualEquity)}
                </span>
              </div>
              {/* Minimum Goal Status */}
              {yearData.minimumGoalPnL && (
                <div className="progress-stat">
                  <span className="label">Minimum Goal (Dec 31):</span>
                  <span className="value">{formatCurrency(yearData.endMinimumEquity)}</span>
                  <span className={`status-badge ${yearData.actualEquity >= yearData.currentMinimumEquity ? 'positive' : 'negative'}`} style={{ marginLeft: '10px' }}>
                    {yearData.actualEquity >= yearData.currentMinimumEquity ? '✓ On Track' : '⚠ Behind'}
                  </span>
                </div>
              )}
              {/* Target Goal Status */}
              <div className="progress-stat">
                <span className="label">Target Goal (Dec 31):</span>
                <span className="value">{formatCurrency(yearData.endTargetEquity)}</span>
                <span className={`status-badge ${yearData.actualEquity >= yearData.targetEquity ? 'positive' : 'negative'}`} style={{ marginLeft: '10px' }}>
                  {yearData.actualEquity >= yearData.targetEquity ? '✓ On Track' : '⚠ Behind'}
                </span>
              </div>
              {/* Stretch Goal Status */}
              {yearData.stretchGoalPnL && (
                <div className="progress-stat">
                  <span className="label">Stretch Goal (Dec 31):</span>
                  <span className="value">{formatCurrency(yearData.endStretchEquity)}</span>
                  <span className={`status-badge ${yearData.actualEquity >= yearData.currentStretchEquity ? 'positive' : 'negative'}`} style={{ marginLeft: '10px' }}>
                    {yearData.actualEquity >= yearData.currentStretchEquity ? '✓ On Track' : '⚠ Behind'}
                  </span>
                </div>
              )}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={yearData.chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis 
                  domain={[
                    Math.max(0, yearData.startEquity * 0.9), // Start 10% below starting equity
                    'auto' // Auto-calculate max
                  ]}
                />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                {/* Minimum Goal Line */}
                {yearData.minimumGoalPnL && (
                  <Line 
                    type="monotone" 
                    dataKey="minimum" 
                    stroke="#ff6b6b" 
                    strokeDasharray="3 3"
                    name="Minimum Goal"
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                {/* Target Goal Line */}
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="#007bff" 
                  strokeDasharray="5 5"
                  name="Target Goal"
                  strokeWidth={2}
                  dot={false}
                />
                {/* Stretch Goal Line */}
                {yearData.stretchGoalPnL && (
                  <Line 
                    type="monotone" 
                    dataKey="stretch" 
                    stroke="#ff9800" 
                    strokeDasharray="3 3"
                    name="Stretch Goal"
                    strokeWidth={2}
                    dot={false}
                  />
                )}
                {/* Actual Equity Line */}
                <Line 
                  type="monotone" 
                  dataKey="actual" 
                  stroke="#82ca9d" 
                  name="Actual Equity"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Historical Progress Table */}
        <div className="goals-history-section">
        <div className="goals-history-header">
          <h4>Historical Progress</h4>
          <div className="goals-history-controls">
            <select
              value={historyView}
              onChange={(e) => handleHistoryViewChange(e.target.value)}
              className="history-view-select"
            >
              <option value="none">No history</option>
              <option value="days">Days</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months</option>
            </select>
            {historyView !== 'none' && (
              <select
                value={historyPeriods}
                onChange={(e) => handleHistoryPeriodsChange(e.target.value)}
                className="history-periods-select"
              >
                <option value="6">Last 6</option>
                <option value="12">Last 12</option>
                <option value="24">Last 24</option>
                <option value="all">All</option>
              </select>
            )}
          </div>
        </div>

        {historyView !== 'none' && historicalData.length > 0 && (
          <div className="goals-history-table-container">
            <table className="goals-history-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Progress</th>
                  <th>Goal</th>
                  <th>Over/Under</th>
                </tr>
              </thead>
              <tbody>
                {historicalData.map((row, index) => {
                  // Determine which goal and current value to show based on historyView
                  let currentValue = 0;
                  let goalValue = 0;
                  
                  if (historyView === 'days') {
                    // For days, use daily P&L (already calculated in getHistoricalData)
                    currentValue = row.daily || 0;
                    // Daily goal: weekly goal / 5 (trading days per week)
                    if (userSettings.weekly_pnl_goal) {
                      goalValue = parseFloat(userSettings.weekly_pnl_goal) / 5;
                    }
                  } else if (historyView === 'weeks') {
                    currentValue = row.weekly || 0;
                    goalValue = userSettings.weekly_pnl_goal ? parseFloat(userSettings.weekly_pnl_goal) : 0;
                  } else if (historyView === 'months') {
                    currentValue = row.monthly || 0;
                    goalValue = userSettings.monthly_pnl_goal ? parseFloat(userSettings.monthly_pnl_goal) : 0;
                  }
                  
                  const overUnder = currentValue - goalValue;
                  const progressPercent = goalValue > 0 ? (currentValue / goalValue) * 100 : 0;
                  
                  return (
                    <tr key={index}>
                      <td className="history-period-cell">{row.period}</td>
                      <td className="history-progress-cell">
                        <HistoricalProgressBar
                          current={currentValue}
                          goal={goalValue}
                          currencySymbol={currencySymbol}
                        />
                      </td>
                      <td className="history-goal-cell">{formatCurrency(goalValue)}</td>
                      <td className={`history-overunder-cell ${overUnder >= 0 ? 'positive' : 'negative'}`}>
                        {overUnder >= 0 ? '+' : ''}{formatCurrency(overUnder)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

// Goal Card Sub-component - Line-by-line structured layout
function GoalCard({ title, current, goal, loading, goalLabel = null, mode = 'equity', currencySymbol = '€' }) {
  const progress = goal ? (current / goal) * 100 : 0;
  
  // Determine color based on progress and value
  let color = 'gray';
  if (current < 0) {
    color = 'red'; // Negative value
  } else if (progress >= 100) {
    color = 'green'; // Goal achieved
  } else if (progress >= 50) {
    color = 'blue'; // Good progress
  } else {
    color = 'orange'; // Behind schedule
  }
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol}0.00`;
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencySymbol}${formattedNumber}`;
  };

  // Extract period from title (e.g., "Weekly Goal (Equity)" -> "Weekly", "Year Goal (Equity)" -> "Year")
  const periodMatch = title.match(/^(\w+)\s+Goal/);
  const period = periodMatch ? periodMatch[1] : 'Goal';

  return (
    <div className="goal-card">
      {/* Line 1: Header - Weekly Goal */}
      <div className="goal-card-line goal-header-line">
        <span className="goal-header-text">{period} Goal</span>
        {loading && <span className="loading-indicator">...</span>}
      </div>
      
      {/* Line 2: Amount of the weekly goal */}
      <div className="goal-card-line goal-amount-line">
        {formatCurrency(goal)}
      </div>
      
      {/* Line 3: The word "Realized" */}
      <div className="goal-card-line goal-realized-label-line">
        Realized
      </div>
      
      {/* Line 4: The realized equity that week */}
      <div className={`goal-card-line goal-realized-value-line ${current >= 0 ? 'positive' : 'negative'}`}>
        {formatCurrency(current)}
      </div>
      
      {/* Line 5: Progress bar with percentage inside (single instance) */}
      <div className="goal-card-line goal-progress-line">
        <div className="progress-bar-with-text">
          <div 
            className={`progress-bar-fill ${color}`}
            style={{ width: `${Math.min(Math.max(progress, 0), 100)}%` }}
          >
            <span className="progress-bar-percentage">
              {progress.toFixed(1)}%
              {progress >= 100 && ' ✓'}
            </span>
          </div>
        </div>
      </div>
      
      {goalLabel && (
        <div className="goal-card-line goal-label-line">
          {goalLabel}
        </div>
      )}
    </div>
  );
}

// Yearly Goal Progress Comparison Component
function YearlyGoalGauge({ current, goal, loading, goalLabel = null, mode = 'equity', currencySymbol = '€' }) {
  // Calculate actual progress percentage
  const actualProgress = goal ? (current / goal) * 100 : 0;
  
  // Calculate expected progress based on time elapsed in year
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear(), 11, 31);
  const totalDaysInYear = Math.ceil((yearEnd - yearStart) / (1000 * 60 * 60 * 24));
  const daysElapsed = Math.ceil((now - yearStart) / (1000 * 60 * 60 * 24));
  const expectedProgress = (daysElapsed / totalDaysInYear) * 100;
  const expectedAmount = goal ? (goal * expectedProgress / 100) : 0;
  
  // Calculate difference (ahead/behind)
  const progressDiff = actualProgress - expectedProgress;
  const amountDiff = current - expectedAmount;
  const isAhead = progressDiff > 0;
  
  // Format currency
  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    const formattedNumber = num.toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencySymbol}${formattedNumber}`;
  };
  
  // Colors
  const actualColor = actualProgress >= 100 ? '#10b981' : actualProgress >= expectedProgress ? '#3b82f6' : '#ef4444';
  
  return (
    <div className="goal-card yearly-progress-card">
      {/* Header */}
      <div className="goal-card-line goal-header-line">
        <span className="goal-header-text">Year Progress</span>
        {loading && <span className="loading-indicator">...</span>}
      </div>
      
      {/* Goal Amount */}
      <div className="goal-card-line goal-amount-line">
        {formatCurrency(goal)}
      </div>
      
      {/* Actual Progress Section */}
      <div className="year-progress-section">
        <div className="year-progress-label-row">
          <span className="year-progress-label">Actual Progress</span>
          <span className={`year-progress-value ${actualColor === '#10b981' ? 'positive' : actualColor === '#3b82f6' ? 'neutral' : 'negative'}`}>
            {actualProgress.toFixed(1)}%
          </span>
        </div>
        <div className="year-progress-bar-container">
          <div 
            className="year-progress-bar year-progress-bar-actual"
            style={{ 
              width: `${Math.min(Math.max(actualProgress, 0), 100)}%`,
              backgroundColor: actualColor
            }}
          >
            {actualProgress >= 5 && (
              <span className="year-progress-bar-text">
                {formatCurrency(current)}
              </span>
            )}
          </div>
          {actualProgress < 5 && (
            <span className="year-progress-bar-text-outside">
              {formatCurrency(current)}
            </span>
          )}
        </div>
      </div>
      
      {/* Expected Progress Section */}
      <div className="year-progress-section">
        <div className="year-progress-label-row">
          <span className="year-progress-label">Expected Progress</span>
          <span className="year-progress-value expected">
            {expectedProgress.toFixed(1)}%
          </span>
        </div>
        <div className="year-progress-bar-container">
          <div 
            className="year-progress-bar year-progress-bar-expected"
            style={{ 
              width: `${Math.min(Math.max(expectedProgress, 0), 100)}%`,
              backgroundColor: '#94a3b8'
            }}
          >
            {expectedProgress >= 5 && (
              <span className="year-progress-bar-text">
                {formatCurrency(expectedAmount)}
              </span>
            )}
          </div>
          {expectedProgress < 5 && (
            <span className="year-progress-bar-text-outside">
              {formatCurrency(expectedAmount)}
            </span>
          )}
        </div>
        <div className="year-progress-days-info">
          {daysElapsed} of {totalDaysInYear} days ({expectedProgress.toFixed(1)}% of year)
        </div>
      </div>
      
      {/* Difference Indicator */}
      <div className={`year-progress-diff ${isAhead ? 'ahead' : 'behind'}`}>
        <span className="year-progress-diff-icon">{isAhead ? '↑' : '↓'}</span>
        <span className="year-progress-diff-text">
          {isAhead ? 'Ahead' : 'Behind'} by {formatCurrency(Math.abs(amountDiff))} ({Math.abs(progressDiff).toFixed(1)}%)
        </span>
      </div>
      
      {goalLabel && (
        <div className="goal-card-line goal-label-line">
          {goalLabel}
        </div>
      )}
    </div>
  );
}

// Historical Progress Bar Sub-component
function HistoricalProgressBar({ current, goal, currencySymbol = '€' }) {
  const progress = goal ? (current / goal) * 100 : 0;
  const color = progress >= 100 ? 'green' : progress >= 50 ? 'orange' : 'red';
  
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol}0.00`;
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${currencySymbol}${formattedNumber}`;
  };

  return (
    <div className="historical-progress-container">
      <div className="historical-progress-bar">
        <div 
          className={`historical-progress-fill ${color}`}
          style={{ width: `${Math.min(progress, 100)}%` }}
        />
      </div>
      <div className="historical-progress-text">
        <span className="historical-amount">{formatCurrency(current)}</span>
        <span className="historical-percentage">{progress.toFixed(1)}%</span>
      </div>
    </div>
  );
}

export default GoalsDisplay;

